import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, startOfWeek, endOfWeek, eachDayOfInterval, addDays, subDays, addWeeks, subWeeks, addMonths, subMonths, startOfMonth, endOfMonth } from 'date-fns';

import { useAuth } from '../context/AuthContext';
import { useTenant } from '../context/TenantContext';
import { meetingService, Meeting } from '../services/meetingService';
import { userService, User } from '../services/userService';
import { formatTime, parseDateAsUTC } from '../utils/dateUtils';
import { useUserTimezone } from '../hooks/useUserTimezone';
import { getMeetingsForDayWithLayout, getHourMinuteInTimezone, getDateStringInTimezone, PositionedMeeting } from '../utils/calendarLayout';
import { ContextMenu } from '../components/ui/ContextMenu';
import { EditCommentsModal } from '../components/modals/EditCommentsModal';
import { CancelMeetingModal } from '../components/modals/CancelMeetingModal';
import { NoShowModal } from '../components/modals/NoShowModal';
import { FinishTreatmentModal } from '../components/modals/FinishTreatmentModal';
import EmailSendDialog from '../components/dialogs/EmailSendDialog';
import SmsSendDialog from '../components/dialogs/SmsSendDialog';
import toast from 'react-hot-toast';

type ViewMode = 'day' | 'week' | '3days' | 'month';

const CalendarPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { currentTenant, refreshKey } = useTenant();
  const userTimezone = useUserTimezone();

  // Users without all_patients only see their own meetings in the calendar.
  const hasAllPatients = !!user?.user_privilege?.all_patients;
  const calendarTherapistId = hasAllPatients ? undefined : user?.id;

  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [meetings, setMeetings] = useState<Meeting[]>([]);

  // Therapist / "Only Mine" filters (only shown to all_patients users; they
  // filter the meetings already loaded from the server).
  const [therapists, setTherapists] = useState<User[]>([]);
  const [therapistFilter, setTherapistFilter] = useState<number | ''>('');
  const [onlyMine, setOnlyMine] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState<ViewMode>('week');

  // Context menu state
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; meeting: Meeting } | null>(null);

  // Modal states
  const [isEditCommentsModalOpen, setIsEditCommentsModalOpen] = useState(false);
  const [selectedMeetingForComments, setSelectedMeetingForComments] = useState<Meeting | null>(null);
  const [isSavingComments, setIsSavingComments] = useState(false);

  const [isCancelMeetingModalOpen, setIsCancelMeetingModalOpen] = useState(false);
  const [selectedMeetingForCancel, setSelectedMeetingForCancel] = useState<Meeting | null>(null);

  const [isNoShowModalOpen, setIsNoShowModalOpen] = useState(false);
  const [selectedMeetingForNoShow, setSelectedMeetingForNoShow] = useState<Meeting | null>(null);

  const [isFinishTreatmentModalOpen, setIsFinishTreatmentModalOpen] = useState(false);
  const [selectedMeetingForFinish, setSelectedMeetingForFinish] = useState<Meeting | null>(null);

  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [smsDialogOpen, setSmsDialogOpen] = useState(false);
  const [selectedMeetingForContact, setSelectedMeetingForContact] = useState<Meeting | null>(null);

  // Calculate date range for API query based on view mode and current date
  const getDateRangeForView = useCallback((): { startDate: Date; endDate: Date } => {
    switch (viewMode) {
      case 'day':
        return { startDate: currentDate, endDate: currentDate };
      case '3days':
        return {
          startDate: currentDate,
          endDate: addDays(currentDate, 2)
        };
      case 'week':
        return {
          startDate: startOfWeek(currentDate, { weekStartsOn: 1 }),
          endDate: endOfWeek(currentDate, { weekStartsOn: 1 })
        };
      case 'month':
        const monthStart = startOfMonth(currentDate);
        const monthEnd = endOfMonth(currentDate);
        return {
          startDate: startOfWeek(monthStart),
          endDate: endOfWeek(monthEnd)
        };
      default:
        return { startDate: currentDate, endDate: currentDate };
    }
  }, [currentDate, viewMode]);

  // Load meetings data
  useEffect(() => {
    const loadCalendarData = async () => {
      if (!user || !currentTenant) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Calculate exact date range based on view mode and current date
        const { startDate, endDate } = getDateRangeForView();

        // Format dates as ISO strings (YYYY-MM-DD) for server-side filtering
        const startDateStr = format(startDate, 'yyyy-MM-dd');
        const endDateStr = format(endDate, 'yyyy-MM-dd');

        // Load meetings with custom date range for server-side filtering
        const meetingsData = await meetingService.getMeetings(
          currentTenant.id,
          undefined,  // patientId
          calendarTherapistId,  // therapistId (self-only when no all_patients)
          undefined,  // status
          undefined,  // dateRange
          startDateStr,  // startDate
          endDateStr,    // endDate
          true           // excludeCancelled
        );
        setMeetings(meetingsData);
      } catch (err) {
        console.error('Error loading calendar data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load calendar data');
      } finally {
        setLoading(false);
      }
    };

    loadCalendarData();
  }, [user, currentTenant, refreshKey, currentDate, viewMode, getDateRangeForView, calendarTherapistId]);

  const getMeetingColor = (status: string): string => {
    switch (status) {
      case 'active':
        return '#e0e7ff'; // Indigo
      case 'done':
        return '#dcfce7'; // Green
      case 'cancelled':
        return '#fee2e2'; // Red
      case 'noshow':
        return '#fef3c7'; // Amber
      default:
        return '#f3f4f6'; // Gray
    }
  };

  // Load the therapist list for the filter dropdown (all_patients users only).
  useEffect(() => {
    if (!hasAllPatients || !currentTenant) return;
    userService.getUsers(currentTenant.id).then(setTherapists).catch(() => setTherapists([]));
  }, [hasAllPatients, currentTenant]);

  // Apply the client-side therapist / "Only Mine" filters to the loaded meetings.
  const filteredMeetings = meetings.filter((m) => {
    if (!hasAllPatients) return true; // server already scoped to own meetings
    if (onlyMine && user) return m.therapist_id === user.id;
    if (therapistFilter) return m.therapist_id === therapistFilter;
    return true;
  });

  const getMeetingsForDate = (date: Date) => {
    const targetDateStr = getDateStringInTimezone(date, userTimezone);
    return filteredMeetings.filter(meeting =>
      getDateStringInTimezone(parseDateAsUTC(meeting.scheduled_at), userTimezone) === targetDateStr
    );
  };

  const handlePrevious = () => {
    switch (viewMode) {
      case 'day':
        setCurrentDate(subDays(currentDate, 1));
        break;
      case '3days':
        setCurrentDate(subDays(currentDate, 3));
        break;
      case 'week':
        setCurrentDate(subWeeks(currentDate, 1));
        break;
      case 'month':
        setCurrentDate(subMonths(currentDate, 1));
        break;
    }
  };

  const handleNext = () => {
    switch (viewMode) {
      case 'day':
        setCurrentDate(addDays(currentDate, 1));
        break;
      case '3days':
        setCurrentDate(addDays(currentDate, 3));
        break;
      case 'week':
        setCurrentDate(addWeeks(currentDate, 1));
        break;
      case 'month':
        setCurrentDate(addMonths(currentDate, 1));
        break;
    }
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  const handleMeetingClick = (e: React.MouseEvent, meeting: Meeting) => {
    e.stopPropagation();
    setContextMenu({ x: e.clientX, y: e.clientY, meeting });
  };

  const handleMeetingContextMenu = (e: React.MouseEvent, meeting: Meeting) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({ x: e.clientX, y: e.clientY, meeting });
  };

const handleEditNotes = async (newNotes: string) => {
    if (!selectedMeetingForComments) return;
    try {
      setIsSavingComments(true);
      await meetingService.updateMeeting(selectedMeetingForComments.id, {
        notes: newNotes,
      });
      // Reload meetings
      const { startDate, endDate } = getDateRangeForView();
      const startDateStr = format(startDate, 'yyyy-MM-dd');
      const endDateStr = format(endDate, 'yyyy-MM-dd');
      const meetingsData = await meetingService.getMeetings(
        currentTenant!.id,
        undefined,
        calendarTherapistId,
        undefined,
        undefined,
        startDateStr,
        endDateStr,
        true
      );
      setMeetings(meetingsData);
      setIsEditCommentsModalOpen(false);
    } catch (err) {
      console.error('Failed to update notes:', err);
      throw err;
    } finally {
      setIsSavingComments(false);
    }
  };


  const getMeetingTooltip = (meeting: Meeting): string => {
    const startTime = parseDateAsUTC(meeting.scheduled_at);
    const endTime = new Date(startTime.getTime() + meeting.duration_minutes * 60000);
    const startTimeStr = formatTime(startTime, userTimezone);
    const endTimeStr = formatTime(endTime, userTimezone);

    const typeName = meeting.meeting_type?.name || meeting.title;
    const userType = meeting.meeting_type?.user_type;
    const firstLine = userType ? `${typeName} - ${userType}` : typeName;

    let tooltip = firstLine;
    tooltip += `\nPatient: ${meeting.patient.full_name}`;
    tooltip += `\nHM ID: ${meeting.patient.hm_id}`;
    tooltip += `\nTime: ${startTimeStr} - ${endTimeStr}`;
    tooltip += `\nTherapist: ${meeting.therapist.full_name}`;
    tooltip += `\nStatus: ${meeting.status}`;
    if (meeting.notes?.trim()) {
      tooltip += `\nNotes: ${meeting.notes}`;
    }
    return tooltip;
  };

  const getMeetingDisplayText = (meeting: Meeting): string => {
    // Calculate end time
    const startTime = parseDateAsUTC(meeting.scheduled_at);
    const endTime = new Date(startTime.getTime() + meeting.duration_minutes * 60000);

    // Format times in user's timezone using the same utility function as the rest of the app
    const startTimeStr = formatTime(startTime, userTimezone);
    const endTimeStr = formatTime(endTime, userTimezone);

    // Format: "HH:MM - HH:MM patient_hm_id patient_name"
    return `${startTimeStr} - ${endTimeStr} ${meeting.patient.hm_id} ${meeting.patient.full_name}`;
  };

  const contextMenuOptions = contextMenu?.meeting ? [
    ...(contextMenu.meeting.team_url ? [{
      label: 'Start',
      icon: '▶️',
      onClick: async () => {
        const m = contextMenu.meeting;
        const usesRecallBot = m.meeting_type?.user_type === 'doctor' || m.meeting_type?.user_type === 'case_manager';
        if (usesRecallBot && m.id) {
          try {
            await meetingService.startMeeting(m.id);
          } catch (e) {
            console.error('Failed to start Recall bot:', e);
          }
        }
        window.open(m.team_url!, '_blank');
        setContextMenu(null);
      },
    }] : []),
    {
      label: 'View Meeting',
      icon: '👁️',
      onClick: () => {
        navigate(`/patients/${contextMenu.meeting.patient_id}?meeting_id=${contextMenu.meeting.id}`);
        setContextMenu(null);
      },
    },
    ...(contextMenu.meeting.yoman_url ? [{
      label: 'Update in Yoman',
      icon: '📅',
      onClick: () => {
        window.open(contextMenu.meeting.yoman_url!, '_blank');
        setContextMenu(null);
      },
    }] : []),
    ...(contextMenu.meeting.status !== 'noshow' ? [{
      label: 'No Show',
      icon: '⛔',
      onClick: () => {
        setSelectedMeetingForNoShow(contextMenu.meeting);
        setIsNoShowModalOpen(true);
        setContextMenu(null);
      },
    }] : []),
    {
      label: 'Finish Treatment',
      icon: (
        <svg fill="#e23232" width="16px" height="16px" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path d="M4,1A1,1,0,0,0,3,2V22a1,1,0,0,0,2,0V17H20a1,1,0,0,0,1-1V4a1,1,0,0,0-1-1H5V2A1,1,0,0,0,4,1ZM7,15H5V13H7Zm0-4H5V9H7ZM17,5h2V7H17Zm0,4h2v2H17Zm0,4h2v2H17ZM13,5h2V7H13Zm0,4h2v2H13Zm0,4h2v2H13ZM9,5h2V7H9ZM9,9h2v2H9Zm0,4h2v2H9ZM7,5V7H5V5Z" />
        </svg>
      ),
      iconClassName: '',
      onClick: () => {
        setSelectedMeetingForFinish(contextMenu.meeting);
        setIsFinishTreatmentModalOpen(true);
        setContextMenu(null);
      },
    },
    ...(contextMenu.meeting.status !== 'cancelled' ? [{
      label: 'Cancel',
      icon: '🚫',
      onClick: () => {
        setSelectedMeetingForCancel(contextMenu.meeting);
        setIsCancelMeetingModalOpen(true);
        setContextMenu(null);
      },
    }] : []),
    {
      label: 'Send Email',
      icon: '📧',
      onClick: () => {
        setSelectedMeetingForContact(contextMenu.meeting);
        setEmailDialogOpen(true);
        setContextMenu(null);
      },
    },
    {
      label: 'Send SMS',
      icon: '📱',
      onClick: () => {
        setSelectedMeetingForContact(contextMenu.meeting);
        setSmsDialogOpen(true);
        setContextMenu(null);
      },
    },
    {
      label: 'Edit Comments',
      icon: '💬',
      onClick: () => {
        setSelectedMeetingForComments(contextMenu.meeting);
        setIsEditCommentsModalOpen(true);
        setContextMenu(null);
      },
    },
  ] : [];

  const PIXELS_PER_HOUR = 80;

  const renderTimelineView = (days: Date[]) => {
    // Dynamically calculate hour range based on meetings in the user's timezone
    let minHour = 8;
    let maxHour = 18;

    if (filteredMeetings.length > 0) {
      const hours: number[] = [];
      filteredMeetings.forEach(m => {
        const { hour } = getHourMinuteInTimezone(parseDateAsUTC(m.scheduled_at), userTimezone);
        hours.push(hour);
      });
      minHour = Math.min(8, ...hours);
      maxHour = Math.max(18, ...hours);
    }

    const dayStartHour = minHour;
    const dayEndHour = maxHour;
    const hours = Array.from({ length: dayEndHour - dayStartHour + 1 }, (_, i) => dayStartHour + i);

    const positionedMeetingsMap: Record<string, PositionedMeeting[]> = {};
    for (const day of days) {
      const dayKey = format(day, 'yyyy-MM-dd');
      positionedMeetingsMap[dayKey] = getMeetingsForDayWithLayout(
        day,
        filteredMeetings,
        dayStartHour,
        PIXELS_PER_HOUR,
        userTimezone
      );
    }

    const minWidth = days.length === 1 ? 600 : days.length === 3 ? 600 : 800;

    return (
      <div style={{ backgroundColor: '#fff', borderRadius: 8, overflow: 'hidden', height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header — reserve the same scrollbar gutter as the scrollable body below so the
            day-column separators line up (overflowY hidden makes scrollbar-gutter apply). */}
        <div style={{ display: 'grid', gridTemplateColumns: `60px repeat(${days.length}, 1fr)`, minWidth, flexShrink: 0, overflowY: 'hidden', scrollbarGutter: 'stable' }}>
          {/* Time column header */}
          <div style={{ borderBottom: '1px solid #e5e7eb', padding: 12 }}></div>

          {/* Day headers */}
          {days.map((day, i) => {
            const dayStr = getDateStringInTimezone(day, userTimezone);
            const todayStr = getDateStringInTimezone(new Date(), userTimezone);
            const isToday = dayStr === todayStr;
            const dayNum = parseInt(dayStr.split('-')[2], 10);
            const weekday = new Intl.DateTimeFormat('en-US', { weekday: 'short', timeZone: userTimezone }).format(day);
            return (
              <div key={i} style={{
                borderBottom: '1px solid #e5e7eb',
                borderLeft: '1px solid #e5e7eb',
                padding: 12,
                textAlign: 'center',
                fontWeight: isToday ? 600 : 500,
                color: isToday ? '#3b5d6b' : '#000',
                backgroundColor: '#fff'
              }}>
                <div style={{ fontSize: 12 }}>{weekday}</div>
                <div style={{ fontSize: 18 }}>{dayNum}</div>
              </div>
            );
          })}
        </div>

        {/* Scrollable time slots */}
        <div style={{ overflow: 'auto', flex: 1, scrollbarGutter: 'stable' }}>
          <div style={{ display: 'grid', gridTemplateColumns: `60px repeat(${days.length}, 1fr)`, minWidth }}>
            {/* Time column */}
            <div style={{ borderRight: '1px solid #e5e7eb', backgroundColor: '#f9fafb' }}>
              {hours.map(hour => (
                <div key={hour} style={{
                  borderTop: '1px solid #e5e7eb',
                  padding: '8px 4px',
                  fontSize: 10,
                  color: '#6b7280',
                  textAlign: 'right',
                  height: PIXELS_PER_HOUR
                }}>
                  {hour.toString().padStart(2, '0')}:00
                </div>
              ))}
            </div>

            {/* Day cells */}
            {days.map((day) => {
              const dayKey = format(day, 'yyyy-MM-dd');
              const positionedMeetings = positionedMeetingsMap[dayKey] || [];
              const containerHeight = hours.length * PIXELS_PER_HOUR;

              return (
                <div key={dayKey} style={{
                  borderLeft: '1px solid #e5e7eb',
                  position: 'relative',
                  minHeight: containerHeight,
                  backgroundColor: '#fff'
                }}>
                  {/* Hour grid lines */}
                  {hours.map(hour => (
                    <div key={hour} style={{
                      position: 'absolute',
                      top: (hour - dayStartHour) * PIXELS_PER_HOUR,
                      left: 0,
                      right: 0,
                      height: PIXELS_PER_HOUR,
                      borderTop: '1px solid #e5e7eb',
                      pointerEvents: 'none'
                    }} />
                  ))}

                  {/* Positioned meetings */}
                  {positionedMeetings.map(meeting => (
                    <div
                      key={meeting.id}
                      onClick={(e) => handleMeetingClick(e, meeting)}
                      onContextMenu={(e) => handleMeetingContextMenu(e, meeting)}
                      title={getMeetingTooltip(meeting)}
                      style={{
                        position: 'absolute',
                        top: `${meeting.top}px`,
                        left: `${meeting.left}%`,
                        width: `calc(${meeting.width}% - 4px)`,
                        height: `${meeting.height}px`,
                        backgroundColor: getMeetingColor(meeting.status),
                        padding: '4px 6px',
                        borderRadius: 3,
                        fontSize: 11,
                        overflow: 'hidden',
                        cursor: 'pointer',
                        border: '1px solid #000',
                        boxSizing: 'border-box',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-start'
                      }}
                    >
                      <div style={{ fontWeight: 600, fontSize: 10, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {getMeetingDisplayText(meeting)}
                      </div>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const render3DayView = () => {
    const threeDays = [currentDate, addDays(currentDate, 1), addDays(currentDate, 2)];
    return renderTimelineView(threeDays);
  };

  const renderDayView = () => {
    const timelineView = renderTimelineView([currentDate]);

    return (
      <div style={{ display: 'flex', justifyContent: 'center', width: '100%', height: '100%' }}>
        <div style={{ maxWidth: '900px', width: '100%' }}>
          {timelineView}
        </div>
      </div>
    );
  };

  const renderWeekView = () => {
    // Week starts on Monday.
    const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
    const weekEnd = endOfWeek(currentDate, { weekStartsOn: 1 });
    const days = eachDayOfInterval({ start: weekStart, end: weekEnd });
    return renderTimelineView(days);
  };

  const renderMonthView = () => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);
    const startDate = startOfWeek(monthStart);
    const endDate = endOfWeek(monthEnd);
    const days = eachDayOfInterval({ start: startDate, end: endDate });

    return (
      <div style={{ backgroundColor: '#fff', borderRadius: 8, height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Day headers */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          borderBottom: '1px solid #e5e7eb',
          flexShrink: 0
        }}>
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <div key={day} style={{
              padding: 12,
              textAlign: 'center',
              fontSize: 12,
              fontWeight: 600,
              color: '#6b7280'
            }}>
              {day}
            </div>
          ))}
        </div>

        {/* Calendar grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          gridAutoRows: 'minmax(120px, auto)',
          flex: 1,
          overflow: 'auto'
        }}>
          {days.map((day, index) => {
            const dayStr = getDateStringInTimezone(day, userTimezone);
            const todayStr = getDateStringInTimezone(new Date(), userTimezone);
            const currentMonthStr = getDateStringInTimezone(currentDate, userTimezone).substring(0, 7);
            const isCurrentMonth = dayStr.substring(0, 7) === currentMonthStr;
            const isToday = dayStr === todayStr;
            const dayNum = parseInt(dayStr.split('-')[2], 10);
            const dayMeetings = getMeetingsForDate(day);

            return (
              <div
                key={index}
                style={{
                  border: '1px solid #e5e7eb',
                  padding: 8,
                  backgroundColor: isCurrentMonth ? '#fff' : '#f9fafb',
                  opacity: isCurrentMonth ? 1 : 0.5
                }}
              >
                <div style={{
                  fontSize: 12,
                  fontWeight: isToday ? 600 : 400,
                  color: isToday ? '#3b5d6b' : '#000',
                  marginBottom: 4
                }}>
                  {dayNum}
                </div>

                <div style={{ fontSize: 10, overflow: 'hidden' }}>
                  {dayMeetings.slice(0, 3).map(meeting => (
                    <div
                      key={meeting.id}
                      onClick={(e) => handleMeetingClick(e, meeting)}
                      onContextMenu={(e) => handleMeetingContextMenu(e, meeting)}
                      title={getMeetingTooltip(meeting)}
                      style={{
                        backgroundColor: getMeetingColor(meeting.status),
                        padding: '2px 4px',
                        marginBottom: 2,
                        borderRadius: 2,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        cursor: 'pointer',
                        border: '2px solid transparent',
                        boxSizing: 'border-box'
                      }}
                    >
                      {getMeetingDisplayText(meeting)}
                    </div>
                  ))}
                  {dayMeetings.length > 3 && (
                    <div style={{ fontSize: 10, color: '#6b7280', marginTop: 2 }}>
                      +{dayMeetings.length - 3} more
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Month view shows just "July 2026"; other views (day/week/3days) also include the
  // day, e.g. "7 July 2026" (day-first to match DD/MM/YYYY convention). Week view shows
  // the Monday (week start) date.
  const headerDate = viewMode === 'week'
    ? startOfWeek(currentDate, { weekStartsOn: 1 })
    : currentDate;
  const monthName = new Intl.DateTimeFormat('en-GB', {
    ...(viewMode !== 'month' ? { day: 'numeric' } : {}),
    month: 'long',
    year: 'numeric',
    timeZone: userTimezone,
  }).format(headerDate);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <div>Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ color: 'red', padding: '16px', marginBottom: '16px' }}>
        Error loading calendar: {error}
      </div>
    );
  }

  return (
    <div style={{ height: 'calc(100vh - 60px - 48px)', display: 'flex', flexDirection: 'column' }}>
      {/* Filters/Controls */}
      <div style={{
        backgroundColor: '#fff',
        padding: '12px 16px',
        borderRadius: 8,
        marginBottom: 16,
        display: 'flex',
        gap: 16,
        alignItems: 'center',
        flexWrap: 'wrap',
        flexShrink: 0
      }}>
        {/* Therapist + Only Mine filters (all_patients users only) */}
        {hasAllPatients && (
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <select
              value={therapistFilter}
              onChange={(e) => setTherapistFilter(e.target.value ? Number(e.target.value) : '')}
              disabled={onlyMine}
              style={{ padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, backgroundColor: onlyMine ? '#f3f4f6' : '#fff' }}
            >
              <option value="">All therapists</option>
              {therapists.map((t) => (
                <option key={t.id} value={t.id}>{t.full_name}</option>
              ))}
            </select>
            <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13, cursor: 'pointer' }}>
              <input type="checkbox" checked={onlyMine} onChange={(e) => setOnlyMine(e.target.checked)} />
              Only Mine
            </label>
          </div>
        )}

        <div style={{ flex: 1 }} />

        {/* Date with prev/next arrows on either side */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button
            onClick={handlePrevious}
            aria-label="Previous"
            style={{ padding: '4px 10px', cursor: 'pointer', border: 'none', backgroundColor: 'transparent', fontSize: 20, lineHeight: 1, color: '#3b5d6b' }}
          >‹</button>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600, minWidth: 140, textAlign: 'center' }}>{monthName}</h2>
          <button
            onClick={handleNext}
            aria-label="Next"
            style={{ padding: '4px 10px', cursor: 'pointer', border: 'none', backgroundColor: 'transparent', fontSize: 20, lineHeight: 1, color: '#3b5d6b' }}
          >›</button>
        </div>

        {/* View buttons */}
        <div style={{ display: 'flex', gap: 4 }}>
          {[
            { key: 'month', label: 'Month' },
            { key: 'week', label: 'Week' },
            { key: '3days', label: '3 Days' },
            { key: 'day', label: 'Day' }
          ].map((v) => (
            <button
              key={v.key}
              onClick={() => setViewMode(v.key as ViewMode)}
              style={{
                padding: '6px 12px',
                border: viewMode === v.key ? '1px solid #3b5d6b' : '1px solid #d1d5db',
                backgroundColor: viewMode === v.key ? '#3b5d6b' : '#fff',
                color: viewMode === v.key ? '#fff' : '#000',
                borderRadius: 4,
                fontSize: 12,
                fontWeight: 500,
                cursor: 'pointer'
              }}
            >
              {v.label}
            </button>
          ))}
        </div>

        <button
          onClick={handleToday}
          style={{
            padding: '6px 16px',
            border: '1px solid #d1d5db',
            backgroundColor: '#fff',
            borderRadius: 6,
            fontSize: 14,
            fontWeight: 500,
            cursor: 'pointer'
          }}
        >
          Today
        </button>
      </div>

      {/* Calendar Container */}
      <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {viewMode === 'day' && renderDayView()}
        {viewMode === '3days' && render3DayView()}
        {viewMode === 'week' && renderWeekView()}
        {viewMode === 'month' && renderMonthView()}
      </div>

      {/* Context Menu */}
      {contextMenu && (
        <ContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          options={contextMenuOptions}
          onClose={() => setContextMenu(null)}
        />
      )}

      {/* Meeting Detail Modal */}

      {/* Edit Comments Modal */}
      {selectedMeetingForComments && (
        <EditCommentsModal
          isOpen={isEditCommentsModalOpen}
          onClose={() => {
            setIsEditCommentsModalOpen(false);
            setSelectedMeetingForComments(null);
          }}
          initialNotes={selectedMeetingForComments.notes || ''}
          onSave={handleEditNotes}
          isSaving={isSavingComments}
        />
      )}

      {/* Cancel Meeting Modal */}
      {selectedMeetingForCancel && (
        <CancelMeetingModal
          isOpen={isCancelMeetingModalOpen}
          onClose={() => {
            setIsCancelMeetingModalOpen(false);
            setSelectedMeetingForCancel(null);
          }}
          meetingId={selectedMeetingForCancel.id}
          onSuccess={() => {
            // Reload meetings
            const { startDate, endDate } = getDateRangeForView();
            const startDateStr = format(startDate, 'yyyy-MM-dd');
            const endDateStr = format(endDate, 'yyyy-MM-dd');
            meetingService.getMeetings(
              currentTenant!.id,
              undefined,
              calendarTherapistId,
              undefined,
              undefined,
              startDateStr,
              endDateStr,
              true
            ).then(setMeetings);
          }}
        />
      )}

      {/* No Show Modal */}
      {selectedMeetingForNoShow && (
        <NoShowModal
          isOpen={isNoShowModalOpen}
          onClose={() => {
            setIsNoShowModalOpen(false);
            setSelectedMeetingForNoShow(null);
          }}
          meetingId={selectedMeetingForNoShow.id}
          onSuccess={() => {
            // Reload meetings
            const { startDate, endDate } = getDateRangeForView();
            const startDateStr = format(startDate, 'yyyy-MM-dd');
            const endDateStr = format(endDate, 'yyyy-MM-dd');
            meetingService.getMeetings(
              currentTenant!.id,
              undefined,
              calendarTherapistId,
              undefined,
              undefined,
              startDateStr,
              endDateStr,
              true
            ).then(setMeetings);
          }}
        />
      )}

      {/* Finish Treatment Modal */}
      {selectedMeetingForFinish && (
        <FinishTreatmentModal
          isOpen={isFinishTreatmentModalOpen}
          onClose={() => {
            setIsFinishTreatmentModalOpen(false);
            setSelectedMeetingForFinish(null);
          }}
          patientId={selectedMeetingForFinish.patient_id}
          patientName={selectedMeetingForFinish.patient.full_name}
          onSuccess={() => {
            const { startDate, endDate } = getDateRangeForView();
            const startDateStr = format(startDate, 'yyyy-MM-dd');
            const endDateStr = format(endDate, 'yyyy-MM-dd');
            meetingService.getMeetings(
              currentTenant!.id,
              undefined,
              calendarTherapistId,
              undefined,
              undefined,
              startDateStr,
              endDateStr,
              true
            ).then(setMeetings);
          }}
        />
      )}

      {/* Email Send Dialog */}
      {selectedMeetingForContact && (
        <EmailSendDialog
          open={emailDialogOpen}
          onClose={() => {
            setEmailDialogOpen(false);
            setSelectedMeetingForContact(null);
          }}
          meetingId={selectedMeetingForContact.id}
          patientName={selectedMeetingForContact.patient.full_name}
          patientEmail={selectedMeetingForContact.patient.email}
          context="MEETING"
          onSendSuccess={(message) => toast.success(message)}
          onSendError={(error) => toast.error(error)}
        />
      )}

      {/* SMS Send Dialog */}
      {selectedMeetingForContact && (
        <SmsSendDialog
          open={smsDialogOpen}
          onClose={() => {
            setSmsDialogOpen(false);
            setSelectedMeetingForContact(null);
          }}
          meetingId={selectedMeetingForContact.id}
          patientName={selectedMeetingForContact.patient.full_name}
          patientPhone={selectedMeetingForContact.patient.phone ?? null}
          context="MEETING"
          onSendSuccess={(message) => toast.success(message)}
          onSendError={(error) => toast.error(error)}
        />
      )}
    </div>
  );
};

export default CalendarPage;
